import React, { useCallback } from "react";
import IconifyIcon from "../IconifyIcon";
import { Box, Button, CircularProgress, Modal, Paper } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectHasUserPropertySetting,
  selectUser,
  selectUserRole,
} from "../../slices/user";
import api, {
  CreateAccessKeyRequest,
  CreateVerifiableIdPayload,
  RegenerateVerifiableIdUUIDPayload,
} from "../../api";
import { AccessKeyCreateResponse } from "../../api/models/access-key-model";
import LoadingModal from "../Modals/LoadingModal";
import { QRCodeCanvas } from "qrcode.react";
import { selectLabels } from "../../slices/language";
import { showError } from "../../utils/helpers";
import {
  AccessGroupsArray,
  AccessGroupsData,
  AccessGroupsDataAttributes,
} from "../../api/models/access-groups";

const getAccessPointIdsFromGroup = (
  group: AccessGroupsData | AccessGroupsDataAttributes,
): number[] => {
  const relationIds =
    group?.access_points?.map((accessPoint) => accessPoint.id) ?? [];
  const configIds = group?.configuration?.map((config) => config.id) ?? [];
  return [...new Set([...relationIds, ...configIds])];
};

const sameIdList = (a: number[], b: number[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/** Lifetime of the user dynamic QR before regeneration (seconds). */
const USER_DYNAMIC_QR_DURATION_SECONDS = 40;

const UserQrCode = () => {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const labels = useAppSelector(selectLabels);
  const userRole = useAppSelector(selectUserRole);
  const hasSecUserPerms = useAppSelector(
    selectHasUserPropertySetting("sec_user_perms"),
  );
  const isSecondaryHostRestricted =
    userRole === "secondary_host" && !hasSecUserPerms;

  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [accessKey, setAccessKey] =
    React.useState<AccessKeyCreateResponse | null>(null);
  const [validUntil, setValidUntil] = React.useState<string>("");
  const [timeLeft, setTimeLeft] = React.useState(0);
  const [verifiableUuid, setVerifiableUuid] = React.useState<string>("");
  const [accessGroups, setAccessGroups] =
    React.useState<AccessGroupsArray | null>(null);
  const [unitAccessPointIds, setUnitAccessPointIds] = React.useState<number[]>(
    [],
  );
  const [hasValidAccessGroups, setHasValidAccessGroups] = React.useState<
    boolean | null
  >(null);
  const [bootstrapReady, setBootstrapReady] = React.useState(false);

  const regenInFlightRef = React.useRef(false);
  const invitationLoadRef = React.useRef(false);

  const resetAccessKeyState = useCallback(() => {
    setAccessKey(null);
    setVerifiableUuid("");
    setValidUntil("");
    setTimeLeft(0);
    regenInFlightRef.current = false;
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setHasValidAccessGroups(null);
    invitationLoadRef.current = false;
    resetAccessKeyState();
  }, [resetAccessKeyState]);

  const loadUnitAccessPoints = useCallback(async (): Promise<number[]> => {
    if (!user?.property?.id || !user?.unit?.documentId) {
      setUnitAccessPointIds((prev) => (prev.length ? [] : prev));
      return [];
    }

    try {
      const response = await api.unitByIdRequest(
        user.property.id,
        user.unit.documentId,
      );
      const rawAccessPoints =
        response?.data?.access_points ??
        (response?.data as { attributes?: { access_points?: { data?: { id: number }[] } | { id: number }[] } })
          ?.attributes?.access_points;

      const accessPointsList = Array.isArray(rawAccessPoints)
        ? rawAccessPoints
        : Array.isArray((rawAccessPoints as { data?: { id: number }[] })?.data)
          ? (rawAccessPoints as { data: { id: number }[] }).data
          : [];

      const ids = accessPointsList
        .map((accessPoint) => accessPoint?.id)
        .filter((id): id is number => typeof id === "number");

      setUnitAccessPointIds((prev) => (sameIdList(prev, ids) ? prev : ids));
      return ids;
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      setUnitAccessPointIds((prev) => (prev.length ? [] : prev));
      return [];
    }
  }, [
    dispatch,
    labels.generic_error,
    user?.property?.id,
    user?.unit?.documentId,
  ]);

  const getAccessGroupsIdsForUserKey = useCallback(
    (unitIds: number[]): number[] => {
      if (unitIds.length === 0) return [];

      const unitIdSet = new Set(unitIds);
      const hasPersonalAccessGroups = (user?.user_access_groups ?? []).some(
        (item) => item?.access === true && item?.access_group?.id,
      );

      const candidateGroups: (AccessGroupsData | AccessGroupsDataAttributes)[] =
        hasPersonalAccessGroups
          ? (user?.user_access_groups ?? [])
              .filter(
                (item) => item?.access === true && item?.access_group?.id,
              )
              .map((item) => item.access_group)
          : (accessGroups?.data ?? []);

      return candidateGroups
        .filter((group) =>
          getAccessPointIdsFromGroup(group).some((id) => unitIdSet.has(id)),
        )
        .map((group) => group.id)
        .filter((id): id is number => typeof id === "number");
    },
    [accessGroups?.data, user?.user_access_groups],
  );

  const getInvitationData2 = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await api.accessKeysByUserIdRequest(user.id);
      if (response && response.length > 0) {
        setAccessKey(response[0]);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  }, [dispatch, labels.generic_error, user?.id]);

  const getUserAccessGroups = useCallback(async () => {
    if (!user?.property?.id) return;

    let roleName = "";
    if (user?.role?.name.startsWith("host")) {
      roleName = "host";
    } else if (user?.role?.name.startsWith("secondary")) {
      roleName = "secondary";
    } else if (user?.role?.name.startsWith("property")) {
      roleName = "property";
    }

    try {
      const response = await api.userAccessGroupsRequest(
        user.property.id,
        roleName,
      );
      if (response) {
        setAccessGroups(response);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  }, [dispatch, labels.generic_error, user?.property?.id, user?.role?.name]);

  React.useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setBootstrapReady(false);
      await Promise.all([getUserAccessGroups(), loadUnitAccessPoints()]);
      if (!cancelled) {
        setBootstrapReady(true);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [getUserAccessGroups, loadUnitAccessPoints]);

  const createUserAccessKey = useCallback(
    async (accessGroupsIds: number[]) => {
      if (!user?.id || accessGroupsIds.length === 0) return;

      try {
        const payload: CreateAccessKeyRequest = {
          created: user.id,
          verified_visitor: user.id,
          access_key_type: "user",
          property: user?.property?.id,
          unit: user?.unit?.id,
          access_groups: accessGroupsIds,
          dynamic_duration: USER_DYNAMIC_QR_DURATION_SECONDS,
        };
        const createdInvitationResponse =
          await api.createAccessKeyRequest(payload);
        if (createdInvitationResponse?.id) {
          const verifiablePayload: CreateVerifiableIdPayload = {
            data: {
              type: "qrcode",
              access_key_qr: createdInvitationResponse.id,
            },
          };
          const verifiableIdResponse =
            await api.createVerifiableId(verifiablePayload);
          if (verifiableIdResponse) {
            setHasValidAccessGroups(true);
            await getInvitationData2();
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    },
    [
      dispatch,
      getInvitationData2,
      labels.generic_error,
      user?.id,
      user?.property?.id,
      user?.unit?.id,
    ],
  );

  const getInvitationData = useCallback(async () => {
    if (!user?.id) return;

    setHasValidAccessGroups(null);
    resetAccessKeyState();

    try {
      const unitIds =
        unitAccessPointIds.length > 0
          ? unitAccessPointIds
          : await loadUnitAccessPoints();
      const accessGroupsIds = getAccessGroupsIdsForUserKey(unitIds);

      if (accessGroupsIds.length === 0) {
        setHasValidAccessGroups(false);

        const response = await api.accessKeysByUserIdRequest(user.id);
        if (response?.length > 0) {
          const currentAccessGroupsIds = (response[0]?.access_groups ?? [])
            .map((group: { id?: number }) => group?.id)
            .filter((id): id is number => typeof id === "number");

          if (currentAccessGroupsIds.length > 0) {
            const payload: CreateAccessKeyRequest = {
              access_groups: [],
              dynamic_duration: USER_DYNAMIC_QR_DURATION_SECONDS,
            };
            await api.updateAccessKeyRequest(response[0]?.id, payload);
          }
        }

        return;
      }

      const response = await api.accessKeysByUserIdRequest(user.id);
      if (response && response.length > 0) {
        const currentAccessGroupsIds = (response[0]?.access_groups ?? [])
          .map((group: { id?: number }) => group?.id)
          .filter((id): id is number => typeof id === "number");
        const same = sameIdList(accessGroupsIds, currentAccessGroupsIds);
        const needsDurationUpdate =
          response[0]?.dynamic_duration !== USER_DYNAMIC_QR_DURATION_SECONDS;

        if (!same || needsDurationUpdate) {
          const payload: CreateAccessKeyRequest = {
            access_groups: accessGroupsIds,
            dynamic_duration: USER_DYNAMIC_QR_DURATION_SECONDS,
          };
          await api.updateAccessKeyRequest(response[0]?.id, payload);
          await getInvitationData2();
        } else {
          setAccessKey(response[0]);
        }

        setHasValidAccessGroups(true);
      } else {
        await createUserAccessKey(accessGroupsIds);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      setHasValidAccessGroups(false);
    }
  }, [
    createUserAccessKey,
    dispatch,
    getAccessGroupsIdsForUserKey,
    getInvitationData2,
    labels.generic_error,
    loadUnitAccessPoints,
    resetAccessKeyState,
    unitAccessPointIds,
    user?.id,
  ]);

  React.useEffect(() => {
    if (!openModal || !bootstrapReady) return;
    if (invitationLoadRef.current) return;

    invitationLoadRef.current = true;
    getInvitationData();
  }, [bootstrapReady, getInvitationData, openModal]);

  const regenerateUuid = useCallback(async () => {
    const uuid =
      accessKey?.verifiable_ids?.[0]?.uuid || accessKey?.qr_uuid || "";

    if (!accessKey?.id || !uuid) return;
    if (regenInFlightRef.current) return;

    regenInFlightRef.current = true;

    try {
      setIsLoading(true);

      const payload: RegenerateVerifiableIdUUIDPayload = {
        data: { id: uuid },
      };

      const regenerateResponse = await api.regenerateVerifiableId(
        accessKey.id,
        payload,
      );

      if (regenerateResponse?.verifiable_id) {
        setValidUntil(regenerateResponse.verifiable_id.valid_until);
        setVerifiableUuid(regenerateResponse.verifiable_id.uuid);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ?? labels.generic_error);
    } finally {
      setIsLoading(false);
      regenInFlightRef.current = false;
    }
  }, [
    accessKey?.id,
    accessKey?.qr_uuid,
    accessKey?.verifiable_ids,
    dispatch,
    labels.generic_error,
  ]);

  React.useEffect(() => {
    if (openModal && accessKey && hasValidAccessGroups) {
      regenerateUuid();
    }
  }, [accessKey, hasValidAccessGroups, openModal, regenerateUuid]);

  React.useEffect(() => {
    if (!openModal || !validUntil || !hasValidAccessGroups) return;

    const target = new Date(validUntil).getTime();
    if (Number.isNaN(target)) return;

    let timer: ReturnType<typeof setInterval>;

    const update = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setTimeLeft(0);
        clearInterval(timer);
        if (openModal) {
          regenerateUuid();
        }
      } else {
        setTimeLeft(diff);
      }
    };

    update();
    timer = setInterval(update, 1000);

    return () => clearInterval(timer);
  }, [validUntil, openModal, regenerateUuid, hasValidAccessGroups]);

  const formatMMSS = (ms: number) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  return (
    <div>
      {isSecondaryHostRestricted ? (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenModal(true)}
        >
          {labels.generate_access_key}
        </Button>
      ) : (
        <IconifyIcon
          icon={"material-symbols:qr-code"}
          fontSize={28}
          className="primary-color pointer"
          onClick={() => setOpenModal(true)}
        />
      )}
      <LoadingModal open={isLoading} setOpen={setIsLoading} />
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            style={{
              maxWidth: 350,
              minWidth: 350,
              width: "40%",
              boxSizing: "border-box",
              paddingTop: 20,
              paddingBottom: 20,
              paddingRight: 30,
              paddingLeft: 30,
            }}
          >
            <div className="flex align-items-center justify-content-between">
              <p className="body-text-md font-w-500">Llave dinámica</p>
              <IconifyIcon
                icon={"material-symbols:close"}
                fontSize={24}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseModal();
                }}
                style={{ cursor: "pointer" }}
              />
            </div>
            {hasValidAccessGroups === false ? (
              <p className="body-text-md text-center mt-20 mb-20">
                {labels.no_access_points_for_user_key}
              </p>
            ) : (
              <>
                <div className="w-100 flex justify-content-center mb-10 mt-10">
                  {verifiableUuid ? (
                    <QRCodeCanvas value={verifiableUuid} size={220} />
                  ) : (
                    <div
                      style={{ width: 220, height: 220 }}
                      className="flex align-items-center justify-content-center"
                    >
                      <CircularProgress />
                    </div>
                  )}
                </div>
                <p className="body-text-xl font-w-500 text-center">
                  {labels.qr_code_expires}: {formatMMSS(timeLeft)}
                </p>
              </>
            )}
          </Paper>
        </Box>
      </Modal>
    </div>
  );
};

export default UserQrCode;
